import React, { useRef, type JSX } from 'react';
import { Box } from '@mui/material';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { CustomTabsSkeleton, EditDashboardDetails, MappedTabs, SelectDashboardModal } from './';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomTabs, CustomTransComponent, Loader } from '../../components';
import Dashboard from '@mui/icons-material/Dashboard';
import Edit from '@mui/icons-material/Edit';

import {
    useGetActiveDashboardsQuery,
    useGetAllTabsQuery,
} from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';

import { mqttTopics, resetDashboard } from '@fiji/common/src/features/dashboardManagement/commonDashboardSlice';
import {
    closeMqttConnection,
    connectMQTT,
    subscribeTopic,
    unsubscribeTopic,
} from '@fiji/common/src/mqtt_connection/mqttConnection';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useIsMount, useRBAC } from 'hooks';
import { CustomMenu } from 'components/CustomMenu';
import {
    setSelectedNode,
    setSelectedNodeLoader,
    setMqttConnectionStatus,
    setMqttCredentials,
    setTabsList,
} from '@fiji/common/src/features/common/commonSlice';
import { DriveFileRenameOutline } from '@mui/icons-material';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { UserProfile } from '@fiji/common/src/types';
import { useGetMQTTCredentialsQuery } from '@fiji/common/src/features/mqtt/mqttApi';
import { decryptAES } from 'utils/helpers';
import { DeviceMenu } from '@fiji/web/src/pages/OrgHome/Devices/DeviceOptions';

export const DashboardManagement = (): JSX.Element => {
    const isMount = useIsMount();
    const editRef: any = React.useRef(null);
    const { deviceId } = useParams();
    const dispatch = useAppDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const activeDashboardId = useTypedSelector((rootState: any) => rootState.commonDashboard.dashboardId);
    const currentRealmName = useTypedSelector((rootState: any) => rootState.common.selectedRealm);
    const navigate = useNavigate();
    const { hash } = useLocation();
    const selectModalRef = useRef<any>(null);
    const permission = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permission);
    const canUpdateSystemDashboard = hasPermission('dashboard-management_edit-system-dashboards');
    const canUpdateUserDashboard = hasPermission('dashboard-management_edit-user-dashboards');
    const {
        currentData: deviceData,
        isFetching: deviceDetailsFetcher,
        refetch: refetchDeviceCall,
    }: any = useGetDeviceByIdQuery(deviceId, {
        refetchOnMountOrArgChange: true,
    });
    const { data: profileData } = useGetUserProfileQuery({}, { refetchOnMountOrArgChange: true }) as ApiEndpointType<
        ApiResponseType<UserProfile>
    >;
    const topics = useTypedSelector(mqttTopics);

    const selectedNode = useTypedSelector((state) => state['common']['selectedNode']);

    const {
        currentData: dashboards,
        isLoading,
        isFetching,
    }: any = useGetActiveDashboardsQuery(
        {
            params: {
                resourceType: 'DEVICE',
                resourceTypeId: deviceId,
            },
        },
        {
            skip: !currentOrg?.id || !deviceId,
            refetchOnMountOrArgChange: true,
        }
    );
    const { data: mqttEncryptedData }: any = useGetMQTTCredentialsQuery();
    const {
        data: currentData,
        isLoading: tabsLoader,
        isFetching: tabsFetcher,
    } = useGetAllTabsQuery(
        { resourceType: 'DEVICE', resourceTypeId: deviceData?.data?.id },
        { skip: !deviceData, refetchOnMountOrArgChange: true }
    );

    const [tabsData, setTabsData] = React.useState<any>([]);

    React.useEffect(() => {
        // if (currentData?.length) {
        //     setTabsData(MappedTabs({ deviceId })?.filter((item: any) => currentData?.includes(item.id)));
        // }
        if (currentData?.data?.tabs) {
            const tabsConfig = currentData?.data?.tabs ?? {};
            setTabsData(MappedTabs({ deviceId, tabsConfig })?.filter((item: any) => tabsConfig?.[item.id]));
            dispatch(setTabsList(Object.keys(tabsConfig)));
        }
    }, [currentData]);

    React.useEffect(() => {
        if (tabsData?.length && (!selectedTab || selectedTab === '')) {
            setSelectedTab(tabsData[0]?.id);
        }
    }, [tabsData]);

    React.useEffect(() => {
        if (mqttEncryptedData?.data && profileData) {
            const decryptedCredentials = decryptAES(mqttEncryptedData?.data, {
                userId: profileData?.data?.id,
                requestId: mqttEncryptedData?.requestId,
            });
            if (decryptedCredentials?.hostname !== window.APP_CONFIG.API_URL?.replace('https://', '')) {
                decryptedCredentials['hostname'] = window.APP_CONFIG.API_URL?.replace('https://', '');
            }
            dispatch(setMqttCredentials({ ...decryptedCredentials }));
        }
    }, [mqttEncryptedData, profileData]);

    React.useEffect(() => {
        dispatch(setSelectedNodeLoader(deviceDetailsFetcher));
    }, [deviceDetailsFetcher]);

    React.useEffect(() => {
        dispatch(setSelectedNode(deviceData?.data));
    }, [deviceData]);

    React.useEffect(
        () => () => {
            if (!isMount) {
                closeMqttConnection();
                dispatch(setMqttConnectionStatus('closed-device'));
            }
        },
        []
    );

    React.useEffect(
        () => () => {
            unsubscribeTopic(topics);
        },
        [deviceId]
    );
    React.useEffect(
        () => () => {
            dispatch(resetDashboard());
        },
        [deviceId, hash]
    );

    /**
     * Refs that always mirror the latest values, so the MQTT handler can:
     *  - read the freshest `selectedNode` when cloning (no stale-closure tree-shift), and
     *  - compare against the CURRENT device's expected STATUS topic (preserves the
     *    FIJI-71085 fix of only updating the banner for the displayed device, even if a
     *    listener leaked from a prior device subscription).
     */
    const selectedNodeRef = React.useRef(selectedNode);
    React.useEffect(() => {
        selectedNodeRef.current = selectedNode;
    }, [selectedNode]);

    const expectedStatusTopicRef = React.useRef<string | undefined>(undefined);

    // Build the exact STATUS topic for the device currently shown.
    const statusTopic = React.useMemo(() => {
        const realmId = profileData?.data?.currentRealmId;
        const orgId = currentOrg?.id;
        const groupId = deviceData?.data?.groupId;
        if (!realmId || !orgId || !groupId || !deviceId) return undefined;
        return `BSSRM/STATUS/${realmId}/${orgId}/${groupId}/${deviceId}`;
    }, [profileData?.data?.currentRealmId, currentOrg?.id, deviceData?.data?.groupId, deviceId]);

    React.useEffect(() => {
        expectedStatusTopicRef.current = statusTopic;
    }, [statusTopic]);

    /**
     * Stable handler: reads latest state via refs so changing `selectedNode` does NOT
     * cause re-subscription (which previously churned a new listener per MQTT message).
     *
     * Preserves FIJI-71085: only the CURRENT device's STATUS messages update the banner.
     * Even if a stale listener from a previous device fires, `expectedStatusTopicRef`
     * holds the CURRENT topic, so mismatched messages are dropped — banner stays correct.
     */
    const mqttResponseHandler = React.useCallback(
        (message: any, messageTopic: any): void => {
            const expectedTopic = expectedStatusTopicRef.current;
            if (!expectedTopic || !messageTopic?.includes(expectedTopic)) return;

            const currentNode = selectedNodeRef.current;
            if (!currentNode) return;

            dispatch(
                setSelectedNode({
                    ...currentNode,
                    deviceStatus: message?.['deviceStatus'],
                    connectionStatus: message?.['connectionStatus'],
                })
            );
        },
        [dispatch]
    );

    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);

    const mqttCredentials = useTypedSelector((state) => state['common']['mqttCredentials']);

    React.useEffect(() => {
        if (
            mqttCredentials &&
            Object.keys(mqttCredentials)?.length &&
            (mqttConnectionStatus === false || mqttConnectionStatus === 'closed-group')
        ) {
            dispatch(setMqttConnectionStatus('loading'));

            connectMQTT(
                () => {
                    dispatch(setMqttConnectionStatus(true));
                },
                {
                    ...mqttCredentials,
                    protocol: 'wss',
                }
            );
        }
    }, [mqttCredentials, mqttConnectionStatus]);

    /**
     * Subscribe to the device's STATUS topic and ALWAYS unsubscribe the exact same
     * topic on cleanup. This prevents the previous device's MQTT listener from
     * remaining attached after navigation — which was the root cause of the device
     * tree visibly "shifting back" to the prior selection on the next status/alarm.
     *
     * Depends on `selectedNode?.id` (the id only) instead of the full `selectedNode`
     * object, so this effect does NOT re-fire (and re-subscribe) on every MQTT
     * message — which would otherwise create churn under the per-handler MQTT layer.
     */
    React.useEffect(() => {
        if (
            mqttConnectionStatus !== true ||
            !statusTopic ||
            !profileData?.data ||
            !deviceData?.data ||
            selectedNode?.id !== deviceId
        ) {
            return;
        }

        subscribeTopic(statusTopic, mqttResponseHandler);

        return () => {
            unsubscribeTopic(statusTopic);
        };
    }, [mqttConnectionStatus, statusTopic, selectedNode?.id, deviceId, mqttResponseHandler]);

    const getActionsBtn = (): any => {
        const menuGroups = [];
        if (canUpdateSystemDashboard || canUpdateUserDashboard) {
            menuGroups.push(
                {
                    icon: <DriveFileRenameOutline />,
                    title: <CustomTransComponent translationKey={'COMMON:EDIT_DETAILS'} />,
                    onClick: (): void => editRef?.current?.handleModalAction?.(true, dashboards?.data),
                },
                {
                    title: <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:SELECT_DASHBOARD'} />,
                    icon: <Dashboard />,
                    onClick: (): void => {
                        selectModalRef?.current?.handleModalAction(true, { deviceId });
                    },
                },
                {
                    title: <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:EDIT_DASHBOARD'} />,
                    icon: <Edit />,
                    onClick: (): void => {
                        navigate(`/${currentRealmName}/editDashboard/${deviceId}/${activeDashboardId}`, {
                            state: {
                                assignType: dashboards?.data?.assignType,
                                assignId: deviceId ?? dashboards?.data?.assignTypeIds[0],
                            },
                        });
                    },
                }
            );
        }
        return {
            id: 'profile-menu',
            menuTitle: '',
            menuSubtitle: '',
            menuGroups,
        };
    };

    const [selectedTab, setSelectedTab] = React.useState();

    const onTabChange = (activeTab: string): void => {
        setSelectedTab(tabsData?.find((tab: any) => tab.id === activeTab)?.id ?? '');
    };

    React.useEffect(() => {
        if (selectedTab) {
            refetchDeviceCall();
        }
    }, [selectedTab]);

    const getMenu = (): any => {
        if (selectedTab === 'summary' && activeDashboardId) {
            if (canUpdateSystemDashboard || canUpdateUserDashboard) {
                return <CustomMenu menuList={getActionsBtn()} />;
            }
        }
        if (selectedTab === 'devices') {
            return <DeviceMenu />;
        }
        return <></>;
    };

    return (
        <>
            <span className="position-relative">
                <Box sx={{ backgroundColor: '#f7f8f8', minHeight: '100vh', height: '100%' }}>
                    <CustomTabs
                        tabsData={
                            tabsFetcher || tabsLoader || (selectedTab === 'summary' && deviceDetailsFetcher)
                                ? CustomTabsSkeleton()
                                : tabsData
                        }
                        actions={getMenu()}
                        variableHeader
                        showExtraTabsInMenu
                        initialTab={tabsData?.[0]?.id}
                        onTabChange={onTabChange}
                    />
                </Box>

                {(canUpdateSystemDashboard || canUpdateUserDashboard) && <SelectDashboardModal ref={selectModalRef} />}

                {isLoading && isFetching && <Loader size={60} />}
            </span>
            <EditDashboardDetails ref={editRef} />
        </>
    );
};
